import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ErrorCode } from '@common/http/enums/error-code.enum';
import { cache } from '@common/util/cache.operator';
import { environment } from '@env/environment';
import { WarningOffsetForPasswordExpirationDays } from '@portal-core/auth/constants/warning-offset-for-password-expiration-days.constant';
import { AuthRoute } from '@portal-core/auth/enums/auth-route.enum';
import { LogoutReason } from '@portal-core/auth/enums/logout-reason.enum';
import { InstanceCodeChangeEvent } from '@portal-core/auth/events/instance-code-change.event';
import { CentralInstance } from '@portal-core/auth/models/central-instance.model';
import { ApiService } from '@portal-core/auth/services/api.service';
import { AuthRouteService } from '@portal-core/auth/services/auth-route.service';
import { AuthService } from '@portal-core/auth/services/auth.service';
import { AuthRouteCommand } from '@portal-core/auth/types/auth-route-command.type';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { AutoUnsubscribe } from '@portal-core/util/auto-unsubscribe.decorator';
import { InputObservable } from '@portal-core/util/input-observable.decorator';
import { LoadingState } from '@portal-core/util/loading-state';
import { Observable, Subscription, combineLatest, map, of, switchMap } from 'rxjs';

@Component({
  selector: 'mc-admin-login-card',
  templateUrl: './admin-login-card.component.html',
  styleUrls: ['./admin-login-card.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
@AutoUnsubscribe()
export class AdminLoginCardComponent implements OnInit, AfterViewInit {
  @Input() instanceCode?: string = null;
  @Input() showCentralInstances?: boolean = true;
  @Input() logoutReason?: LogoutReason;

  @Output() instanceCodeChange: EventEmitter<InstanceCodeChangeEvent> = new EventEmitter<InstanceCodeChangeEvent>();

  @InputObservable('instanceCode') instanceCode$: Observable<string>;
  @InputObservable('showCentralInstances') showCentralInstances$: Observable<boolean>;

  /** Applies the mc-admin-login-card class to the host element. */
  @HostBinding('class.mc-admin-login-card') hostClass: boolean = true;

  email: string;
  centralInstances$: Observable<CentralInstance[]>;
  centralInstanceSubscription: Subscription;
  selectedCentralInstance: CentralInstance;
  selectedCentralInstanceSubscription: Subscription;
  forgotPasswordRouteCommand$: Observable<AuthRouteCommand>;
  licenseUrlRouteCommands$: Observable<any[]>;
  loadingState: LoadingState<ErrorCode> = new LoadingState<ErrorCode>();
  loginLoadingState: LoadingState<ErrorCode> = new LoadingState<ErrorCode>();
  madcapUrl: string = environment.madcapUrl + '/free-trials/madcap-central';
  password: string;

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private authRouteService: AuthRouteService,
    private errorService: ErrorService
  ) { }

  ngOnInit() {
    this.updateRoutesForInstance();

    this.licenseUrlRouteCommands$ = this.authRouteService.getAuthRedirect$(AuthRoute.LicenseUrl).pipe(
      map((redirect: AuthRouteCommand) => redirect.commands)
    );

    this.centralInstances$ = this.showCentralInstances$.pipe(
      switchMap(showCentralInstances => {
        if (showCentralInstances) {
          return this.authService.getCentralInstances$();
        } else {
          return of(null);
        }
      }),
      cache()
    );

    // Subscribe to the instance code input and the central instances to update the selected central instance
    this.selectedCentralInstanceSubscription = combineLatest([
      this.instanceCode$,
      this.centralInstances$
    ]).subscribe(([instanceCode, centralInstances]) => {
      // Update the selected central instance by using the instanceCode input and falling back to the first central instance
      this.setSelectedCentralInstance(centralInstances?.find(centralInstance => centralInstance.InstanceCode === instanceCode) ?? centralInstances?.[0], !instanceCode);
    });

    // Subscribe to the API's central instance changing to update the routes for the current instance
    this.centralInstanceSubscription = this.apiService.centralInstance$.subscribe(() => this.updateRoutesForInstance());

    // Start the initial load which is just fetching the central instances
    this.loadingState.update(true);

    this.centralInstances$.subscribe({
      next: () => {
        this.loadingState.update(false);
      },
      error: error => {
        this.loadingState.update(false, ErrorCode.GenericLoadCentralInstancesError, this.errorService.getErrorCodes(error));
      }
    });
  }

  /**
   * Required by TranslationString
   */
  ngAfterViewInit() {
    if (this.logoutReason === LogoutReason.IdleTimeExpiration) {
      this.loginLoadingState.update(false, ErrorCode.IdleLogoutError);
    }
  }

  onSelectedCentralInstanceChanged(centralInstance: CentralInstance) {
    this.setSelectedCentralInstance(centralInstance);
  }

  login(loginForm: NgForm) {
    if (!loginForm.form.valid) {
      return;
    }

    this.loginLoadingState.update(true);

    // Store the instance code to use later if the user is redirected to the expired password page
    const instanceCode = this.apiService.centralInstanceInstanceCode;

    this.authService.login$(this.email, this.password).pipe(
      // Redirect the user now that they are logged in
      switchMap(auth => {
        const daysUntilPasswordExpires = auth.daysUntilPasswordExpires === '' ? null : Number(auth.daysUntilPasswordExpires);

        // If the user's password is about to expire or is expired
        if (daysUntilPasswordExpires && daysUntilPasswordExpires <= WarningOffsetForPasswordExpirationDays) {
          // Then navigate the user to the expired password page
          return this.authRouteService.navigateToAuthRoute$(AuthRoute.ExpiredPassword, { queryParams: { email: auth.userName } });
        } else {
          // If the logout reason is from an idle timeout then return the user back to the last page they were on
          if (this.logoutReason === LogoutReason.IdleTimeExpiration) {
            return this.authRouteService.navigateToAuthRoute$(AuthRoute.LastActivatedRoute);
          } else {
            return this.authRouteService.navigateToAuthRoute$(AuthRoute.LicenseList);
          }
        }
      })
    ).subscribe(() => {
      this.authService.emitLoginComplete();
    }, error => {
      if (this.errorService.hasErrorCode(error, ErrorCode.UserExpiredPasswordError)) {
        this.authRouteService.navigateToAuthRoute$(AuthRoute.ExpiredPassword, { queryParams: { email: this.email } }, { instanceCode }).subscribe(null, () => {
          // Something went wrong with the navigation so display an error
          this.loginLoadingState.update(false, ErrorCode.GenericLoginError);
        });
      } else {
        this.loginLoadingState.update(false, ErrorCode.GenericLoginError, this.errorService.getErrorCodes(error));
      }
    });
  }

  private setSelectedCentralInstance(centralInstance: CentralInstance, defaulted: boolean = false) {
    this.selectedCentralInstance = centralInstance;

    // If the instance code of the selected instance doesn't match the current instance code then update it
    if (this.instanceCode !== this.selectedCentralInstance?.InstanceCode) {
      this.instanceCode = this.selectedCentralInstance?.InstanceCode;
      this.instanceCodeChange.emit({
        defaulted,
        instanceCode: this.instanceCode
      });
    }
  }

  private updateRoutesForInstance() {
    this.forgotPasswordRouteCommand$ = this.authRouteService.getAuthRedirect$(AuthRoute.ForgotPassword) as Observable<AuthRouteCommand>;
  }
}